import { SelectOption } from "../../component/customSelect/customSelect"
import { SubjectSelectOption } from "../../types/subjectSelect"
import { QuestionStatus, SubPart } from "../../api/type"
import { getYearValidationError } from "../../utils/yearValidation"
import {
  validateQuestionHierarchyLevels,
} from "../Questions/utils/hierarchyValidation"

export interface DocumentData {
  type: string
  content: string
}

export interface QuestionDocument {
  question_result: { data: DocumentData[] }
  solution_result: { data: DocumentData[] }
  answer_result: { data: DocumentData[] }
  marking_scheme_result?: { data: DocumentData[] } | null
}

export interface BulkQuestionData {
  subject: string
  topic: string
  topic_id?: string | null
  chapter?: string
  chapter_id?: string | null
  level_2_id?: string | null
  level_2_name?: string | null
  level_3_id?: string | null
  level_3_name?: string | null
  level_4_id?: string | null
  level_4_name?: string | null
  complexity: string
  grade: string
  source?: string | null
  year?: number | null
  marks?: number | null
  sub_parts_labels?: string | null
  documents: QuestionDocument[]
  bulk_session_id: string
}

export interface ProcessedQuestionContent {
  question: string
  solution: string
  answer: string
  marking_scheme: string
  subject: string
  chapter: string
  chapter_id?: string | null
  topic: string
  topic_id?: string | null
  level_2_id?: string | null
  level_2_name?: string | null
  level_3_id?: string | null
  level_3_name?: string | null
  level_4_id?: string | null
  level_4_name?: string | null
  complexity: string
  grade: string
  source: string
  year: string
  marks: string
  subPartsText: string
  sub_parts: SubPart[]
  question_status: QuestionStatus
  question_result?: { data: DocumentData[] }
  solution_result?: { data: DocumentData[] }
  answer_result?: { data: DocumentData[] }
  marking_scheme_result?: { data: DocumentData[] } | null
}

export type PreviewSubjectOption = SubjectSelectOption

export type OcrTabType =
  | "question_result"
  | "solution_result"
  | "answer_result"

export type PreviewTabType = OcrTabType | "marking_scheme"

/** @deprecated Use OcrTabType (legacy) or PreviewTabType (preview) */
export type TabType = OcrTabType

export type PreviewFieldErrors = {
  subject?: string
  chapter?: string
  topic?: string
  level_2?: string
  level_3?: string
  level_4?: string
  grade?: string
  complexity?: string
  question?: string
  year?: string
}

export type PreviewQuestionValidationError = {
  index: number
  errors: PreviewFieldErrors
}

export const getQuestionFieldErrors = (
  item: ProcessedQuestionContent,
  targetStatus: QuestionStatus,
  levelLabels?: string[]
): PreviewFieldErrors => {
  const errors: PreviewFieldErrors = {}

  if (!item.subject) errors.subject = "Subject is required."

  if (levelLabels && levelLabels.length) {
    const levelErrors = validateQuestionHierarchyLevels(
      {
        level_1: item.topic_id || item.topic || "",
        level_2: item.level_2_id || "",
        level_3: item.level_3_id || "",
        level_4: item.level_4_id || "",
      },
      levelLabels
    )
    if (levelErrors.level_1) errors.topic = levelErrors.level_1
    if (levelErrors.level_2) errors.level_2 = levelErrors.level_2
    if (levelErrors.level_3) errors.level_3 = levelErrors.level_3
    if (levelErrors.level_4) errors.level_4 = levelErrors.level_4
  } else {
    if (!item.topic_id && !item.topic) errors.topic = "Topic is required."
  }

  if (!item.grade) errors.grade = "Level is required."
  if (!item.complexity) errors.complexity = "Difficulty is required."
  if (targetStatus === "published" && !item.question?.trim()) {
    errors.question = "Question text is required to publish."
  }

  const yearError = getYearValidationError(item.year)
  if (yearError) errors.year = yearError

  return errors
}

export const hasPreviewFieldErrors = (errors: PreviewFieldErrors) =>
  Object.values(errors).some(Boolean)

export type { SelectOption }
